/**
 * How many bottles are left, and the two buttons that change it.
 *
 * The count is held locally while the write is in flight rather than waiting
 * for the refetch: a stepper that does not move until the server has answered
 * reads as broken. The write sends the absolute number rather than a delta —
 * the browser already knows what it is showing — so a fast double-tap settles
 * on the number on screen instead of drinking two bottles.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api } from '../lib/api'

/** Mirrors `store.MAX_BOTTLE_COUNT`; the server clamps, this just stops asking. */
const MAX = 99

export default function Inventory({
  wineId,
  count,
  className = '',
}: {
  wineId: string
  count: number
  className?: string
}) {
  const client = useQueryClient()
  const [pending, setPending] = useState<number | null>(null)
  const shown = pending ?? count

  const save = useMutation({
    mutationFn: (next: number) => api.setInventory(wineId, next),
    onMutate: (next: number) => setPending(next),
    onSettled: async (_data, _error, next) => {
      // The critics read this number and the grid displays it, so both are
      // stale the moment it changes — `staleTime` is 30s and would otherwise
      // hold the old count on screen. Cleared only if nothing newer has been
      // typed over it in the meantime.
      await Promise.all([
        client.invalidateQueries({ queryKey: ['cellar'] }),
        client.invalidateQueries({ queryKey: ['wine', wineId] }),
      ])
      setPending((current) => (current === next ? null : current))
    },
  })

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Step
        label="One fewer bottle"
        disabled={shown <= 0}
        onClick={() => save.mutate(shown - 1)}
      >
        −
      </Step>
      <span
        className={`min-w-0 flex-1 text-center text-[11px] ${
          shown ? 'text-stone-400' : 'text-stone-600'
        }`}
      >
        {shown ? `${shown} left` : 'none left'}
      </span>
      <Step
        label="One more bottle"
        disabled={shown >= MAX}
        onClick={() => save.mutate(shown + 1)}
      >
        +
      </Step>
      {save.isError && (
        <span className="text-[11px] text-rose-300">{(save.error as Error).message}</span>
      )}
    </div>
  )
}

function Step({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-white/10 text-sm
        text-stone-300 transition hover:border-white/25 hover:text-white active:scale-95
        disabled:pointer-events-none disabled:opacity-25"
    >
      {children}
    </button>
  )
}
